import {
  type UserInfoQueryResult,
  type UserStatus,
  getAuthUserInfo,
} from "Infrastructure/Api/Api";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import { areJwtClaimsEqual } from "Utils/JwtUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { put, takeLatest } from "redux-saga/effects";
import { call } from "typed-redux-saga";
import { createAsyncAction } from "typesafe-actions";
import { getType } from "typesafe-actions";

export const getUserInfoAsync = createAsyncAction(
  "@auth/GET_USER_INFO_REQUEST",
  "@auth/GET_USER_INFO_SUCCESS",
  "@auth/GET_USER_INFO_FAILURE",
)<
  { withLoadingStatus?: boolean } | undefined,
  Omit<UserInfoQueryResult, "status"> & { userStatus: UserStatus },
  Error
>();

function* getUserInfo(
  action: ReturnType<typeof getUserInfoAsync.request>,
): Generator {
  try {
    const response = yield* call(getAuthUserInfo);
    if (response.status === 200) {
      // user-info re-mints the JWT (with the active_region claim) on every call — persist it,
      // otherwise a pre-deploy token without the claim keeps 403ing every write for its whole
      // 7-day life. The guard matters: setToken with a missing token DELETES the stored JWT.
      // Persist only on a material claim change: every mint differs in iat/exp, and writing
      // those no-op refreshes fires storage events that ping-pong user-info calls between
      // open tabs (useAuthStateSync reacts to jwtToken changes).
      if (response.data.token) {
        const storedToken = getLocalStorageItem(JWT_KEY);
        if (!areJwtClaimsEqual(storedToken, response.data.token)) {
          setToken(response.data);
        }
      }
      const { status, ...userInfo } = response.data;
      yield put(getUserInfoAsync.success({ ...userInfo, userStatus: status }));
    } else {
      yield put(
        getUserInfoAsync.failure(
          new Error(`Error in action ${action.type}`, {
            cause: response.status,
          }),
        ),
      );
    }
  } catch (err) {
    yield put(getUserInfoAsync.failure(err as Error));
  }
}
export function* getUserInfoSaga() {
  yield takeLatest(getType(getUserInfoAsync.request), getUserInfo);
}
